import {
  NgTemplateOutlet,
  NgClass,
  AsyncPipe,
  CurrencyPipe,
  CommonModule,
} from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatOptionModule, MatRippleModule } from "@angular/material/core";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSortModule } from "@angular/material/sort";
import { FuseAlertComponent, FuseAlertType } from "@fuse/components/alert";
import { API_ROUTE } from "app/api-routes";
import { ApiService } from "app/service/api.service";
import { MatRadioModule } from "@angular/material/radio";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { BehaviorSubject, forkJoin } from "rxjs";
import { TOAST_DURATION } from "app/app-constant";

@Component({
  selector: "app-role-form",
  standalone: true,
  imports: [
    MatProgressBarModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSortModule,
    NgTemplateOutlet,
    MatPaginatorModule,
    NgClass,
    MatSlideToggleModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatRippleModule,
    AsyncPipe,
    CurrencyPipe,
    CommonModule,
    MatChipsModule,
    MatRadioModule,
    MatDialogModule,
    FuseAlertComponent,
    MatTableModule,
  ],
  templateUrl: "./role-form.component.html",
  styleUrl: "./role-form.component.scss",
})
export class RoleFormComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  roleForm!: FormGroup;
  isModularPermissionsExpanded = true;
  alert: { type: FuseAlertType; message: string } = {
    type: "success",
    message: "",
  };
  showAlert: boolean = false;
  roleData: any[] = [];
  selectedModularPermissions: string[] = [];
  modularPermissions: any[] = [];
  roleListSubject = new BehaviorSubject<any[]>([]);
  roleList$ = this.roleListSubject.asObservable();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RoleFormComponent>,
    private _apiService: ApiService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.roleForm = this.fb.group({
      name: ["", [Validators.required, Validators.pattern(/.*\S.*/), Validators.maxLength(100)]],
      department: ["", [Validators.required]],
      desc: ["", [Validators.required]],
      permissionType: ["modular"],
      modularPermission: [[], [Validators.required]],
      functionalPermission: [[], []],
      userRoleType: ["", [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    forkJoin({
      functional: this._apiService.get(API_ROUTE.PERMISSIONS.getFunctional),
      modular: this._apiService.get(API_ROUTE.PERMISSIONS.getModular),
    }).subscribe(({ functional, modular }: any) => {
      this.modularPermissions = modular.data;
      const fullFunctional = functional.data;

      if (this.data) {
        this.roleForm.patchValue(this.data);
        
        if (this.data.functionalPermission?.length) {
          // Use the canonical fullFunctional list as the source of truth for labels,
          // submodule structure, and disabledPermissions. Only carry over `permissions`
          // values from the saved role — matched by submodule name (with key as fallback).
          // Name is used because submodule `key` is the action ("write"/"delete") and is
          // NOT unique within a module — e.g. User Management has Create and Edit both
          // with key="write". Keying the map by `key` collapsed them and caused saved
          // state for one submodule to leak onto siblings on re-edit.
          const savedByKey = new Map<string, any>();
          for (const saved of this.data.functionalPermission) {
            savedByKey.set(saved.key, saved);
          }

          this.roleData = fullFunctional.map((canonical: any) => {
            const canonicalCopy = JSON.parse(JSON.stringify(canonical));
            // The master endpoint may return non-zero default permissions on its
            // submodules. Zero them here so the role's saved state is the ONLY
            // source of truth for what's checked — otherwise unticking a module
            // and saving wouldn't visibly clear it on re-edit (the master
            // defaults would leak back in).
            if (canonicalCopy.subModules?.length) {
              for (const csub of canonicalCopy.subModules) {
                if (csub.permissions) {
                  for (const k of Object.keys(csub.permissions)) {
                    csub.permissions[k] = false;
                  }
                }
              }
            }
            const saved = savedByKey.get(canonical.key);
            if (!saved?.subModules?.length) return canonicalCopy;

            const savedSubByName = new Map<string, any>();
            for (const ssub of saved.subModules) {
              savedSubByName.set(ssub.name || ssub.key, ssub);
            }

            canonicalCopy.subModules = canonicalCopy.subModules.map((csub: any) => {
              const match =
                savedSubByName.get(csub.name) || savedSubByName.get(csub.key);
              if (match?.permissions) {
                return { ...csub, permissions: { ...match.permissions } };
              }
              return csub;
            });
            return canonicalCopy;
          });
        } else {
          this.roleData = this.zeroPermissions(
            JSON.parse(JSON.stringify(fullFunctional))
          );
        }

        if (this.data.modularPermission?.length) {
          this.selectedModularPermissions = this.data.modularPermission;
        }
      } else {
        // Create flow: the master endpoint may return non-zero default
        // permissions on some submodules (e.g. Settings, Re-Assessment Tracker).
        // Zero them so a brand-new role starts with everything unchecked.
        this.roleData = this.zeroPermissions(
          JSON.parse(JSON.stringify(fullFunctional))
        );
      }

      this.processFunctionalPermissions();
      this.syncModularFormControl();
      this.cdr.detectChanges();
    });
  }

  /**
   * Sets every submodule permission to false. Used for the create flow (and edit
   * flows with no saved functional permissions) so the master endpoint's default
   * `true` values don't pre-tick modules like Settings / Re-Assessment Tracker.
   */
  private zeroPermissions(modules: any[]): any[] {
    for (const module of modules) {
      if (!module.subModules?.length) continue;
      for (const sub of module.subModules) {
        if (sub.permissions) {
          for (const k of Object.keys(sub.permissions)) {
            sub.permissions[k] = false;
          }
        }
      }
    }
    return modules;
  }

  processFunctionalPermissions() {
    this.roleData = this.roleData.map((module: any) => ({
      ...module,
      isExpanded: module.isExpanded || false,
      subModules: module.subModules.map((subModule: any) => ({
        ...subModule,
        permissions: { ...subModule.permissions },
      })),
    }));
  }

  /**
   * For each submodule the checkbox should bind to the single action this submodule
   * represents (e.g. Workflow > "Create" represents `write`, "Delete" represents `delete`,
   * Dashboard widgets represent `read`). That action is whichever one is NOT in
   * `disabledPermissions`. Falls back to `read` if everything is disabled.
   */
  getEnabledAction(sub: any): "read" | "write" | "delete" {
    const all: Array<"read" | "write" | "delete"> = ["read", "write", "delete"];
    const disabled = sub?.disabledPermissions ?? [];
    return all.find((a) => !disabled.includes(a)) ?? "read";
  }

  isAtLeastOnePermissionEnabled(module: any): boolean {
    if (!module.subModules || !Array.isArray(module.subModules)) return false;
    return module.subModules.some((sub: any) =>
      Object.values(sub.permissions).some((val) => val === true)
    );
  }

  onModuleCheck(module: any): void {
    const isCurrentlyEnabled = this.isAtLeastOnePermissionEnabled(module);
    const shouldEnable = !isCurrentlyEnabled;

    module.subModules.forEach((sub: any) => {
      Object.keys(sub.permissions).forEach((key) => {
        if (!sub.disabledPermissions?.includes(key)) {
          sub.permissions[key] = shouldEnable;
        }
      });
    });

    if (shouldEnable) module.isExpanded = true;
    this.syncModularFormControl();
    this.cdr.detectChanges();
  }

  togglePermission(subModule: any, permissionType: string, parentModule: any) {
    const newValue = !subModule.permissions[permissionType];
    if (newValue) {
      // When checking, enable all permissions for this submodule
      Object.keys(subModule.permissions).forEach((key) => {
        if (!subModule.disabledPermissions?.includes(key)) {
          subModule.permissions[key] = true;
        }
      });
    } else {
      // When unchecking, disable all permissions for this submodule
      Object.keys(subModule.permissions).forEach((key) => {
        if (!subModule.disabledPermissions?.includes(key)) {
          subModule.permissions[key] = false;
        }
      });
    }
    this.syncModularFormControl();
    this.cdr.detectChanges();
  }

  private syncModularFormControl() {
    const activeKeys = this.roleData
      .filter((module) => this.isAtLeastOnePermissionEnabled(module))
      .map((module) => module.key);
    
    this.selectedModularPermissions = activeKeys;
    this.roleForm.get('modularPermission')?.setValue(activeKeys);
    this.roleForm.get('modularPermission')?.updateValueAndValidity();
  }

  onSubmit() {
    if (this.roleForm.valid) {
      // Create a clean copy of the form values
      const formValue = { ...this.roleForm.value };
      
      // Filter roleData to only include modules that actually have permissions selected
      const finalFunctionalPermissions = this.roleData.filter(module => 
        this.isAtLeastOnePermissionEnabled(module)
      );

      const payload = {
        ...formValue,
        functionalPermission: finalFunctionalPermissions,
        modularPermission: this.selectedModularPermissions
      };

      delete payload.permissionType;

      if (this.data?._id) {
        this._apiService
          .put(API_ROUTE.ROLE.update, { ...payload, id: this.data._id })
          .subscribe(
            (res: any) => {
              this.dialogRef.close(res);
              this.openSnackBar(res.msg || "Updated successfully");
            },
            (err: any) => {
              this.openSnackBar(err.error?.msg || "Update failed");
            }
          );
      } else {
        this._apiService.post(API_ROUTE.ROLE.create, payload).subscribe(
          (res: any) => {
            this.dialogRef.close(res);
            this.openSnackBar(res.msg || "Created successfully");
          },
          (err: any) => {
            this.openSnackBar(err.error?.msg || err.error?.message || "Creation failed");
          }
        );
      }
    } else {
      this.showErrors();
    }
  }

  showErrors() {
    this.roleForm.markAllAsTouched();
    this.cdr.detectChanges();
    if (this.roleForm.get("modularPermission")?.hasError("required")) {
      this.showAlert = true;
      this.alert = { type: "error", message: "At least one permission must be selected." };
    } else {
      this.showAlert = false;
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  openSnackBar(message: string, action: string = "") {
    this._snackBar.open(message, action, {
      horizontalPosition: "end",
      verticalPosition: "top",
      duration: TOAST_DURATION,
      panelClass: "app-snackBar",
    });
  }

  getDisplayPermissionName(name: string): string {
    if (!name) return "";
    const normalized = name.trim().toLowerCase();
    if (normalized === "view my tasks" || normalized === "view my task") {
      return "View";
    }
    return name;
  }
}